/**
 * Cadence — free-form interval + unit, with `'off'` as a first-class paused value.
 *
 * Used by:
 *   - `BrandAuditConfig.cadenceOverride[axis]` (per-brand × per-axis user setting)
 *   - `BrandSchedule.cadence` (cache of the value at last sync, on the brand row)
 *   - `EmailRecipient` (per-recipient digest cadence — added in S5)
 *
 * Design rationale: docs/scheduler-redesign-phases-s2-s6.md §0.2 D-2.
 */
export type Cadence =
  | { interval: number; unit: 'days' | 'weeks' | 'months' }
  | 'off';

/**
 * Audit types that can be scheduled (= every type with a cadence row in
 * `BrandAuditConfig.cadenceOverride`).
 *
 * `brand` (legacy combined type) and `social` are NOT included — `brand` is
 * read-only for historical audits and `social` is not yet implemented. Both
 * can be added later by extending this tuple + tier defaults below.
 */
export const SCHEDULABLE_AUDIT_TYPES = [
  'website',
  'earned_media',
  'brand_narrative',
  'brand_authority',
  'brand_referral',
  'news_watch',
] as const;
export type SchedulableAuditType = (typeof SCHEDULABLE_AUDIT_TYPES)[number];

export function isSchedulableAuditType(t: string): t is SchedulableAuditType {
  return (SCHEDULABLE_AUDIT_TYPES as readonly string[]).includes(t);
}

/**
 * Tier defaults — applied when `brand.auditConfig.cadenceOverride[axis]` is unset.
 *
 * Numbers from docs/pricing-and-billing-plan.md §2.4 (the table is the source
 * of truth). Editing here without updating the doc is a bug.
 */
export const CADENCE_DEFAULTS: Record<string, Record<SchedulableAuditType, Cadence>> = {
  lite: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: 'off',
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
    news_watch: 'off',
  },
  pro: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: { interval: 1, unit: 'days' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
    news_watch: 'off',
  },
  enterprise: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: { interval: 1, unit: 'days' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
    news_watch: { interval: 1, unit: 'weeks' },
  },
};

/**
 * Minimum cadence allowed per tier. Protects Bedrock RPM by preventing
 * sub-day cadences until the agency tier ships.
 *
 * If we ever add `unit: 'hours'`, the agency floor would land here as
 * something like `{ interval: 1, unit: 'hours' }`.
 */
export const CADENCE_FLOOR: Record<string, Cadence> = {
  lite: { interval: 1, unit: 'days' },
  pro: { interval: 1, unit: 'days' },
  enterprise: { interval: 1, unit: 'days' },
};

/** Convert a Cadence to a human-readable French label (UI-facing). */
export function describeCadence(c: Cadence): string {
  if (c === 'off') return 'Désactivé';
  const { interval, unit } = c;
  const singular = { days: 'jour', weeks: 'semaine', months: 'mois' }[unit];
  const plural = { days: 'jours', weeks: 'semaines', months: 'mois' }[unit];
  if (interval === 1) {
    return unit === 'months' ? 'Tous les mois' : `Tous les ${singular}s`;
  }
  return `Tous les ${interval} ${plural}`;
}

/**
 * Resolve effective cadence: brand override → tier default. Returns `'off'`
 * when nothing applies (defensive — every supported tier has every axis
 * covered in `CADENCE_DEFAULTS`).
 */
export function resolveCadence(
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined,
  tier: string,
  axis: SchedulableAuditType,
): Cadence {
  if (cadenceOverride && axis in cadenceOverride) {
    return cadenceOverride[axis] as Cadence;
  }
  return CADENCE_DEFAULTS[tier]?.[axis] ?? 'off';
}

function cadenceToDays(c: Exclude<Cadence, 'off'>): number {
  const mult = c.unit === 'days' ? 1 : c.unit === 'weeks' ? 7 : 30;
  return c.interval * mult;
}

/**
 * Throws when a cadence is faster than the tier floor. Caller wraps in the
 * appropriate framework exception (`BadRequestException` server-side).
 */
export function assertCadenceAllowed(tier: string, cadence: Cadence): void {
  if (cadence === 'off') return;
  const floor = CADENCE_FLOOR[tier];
  if (!floor || floor === 'off') return;
  if (cadenceToDays(cadence) < cadenceToDays(floor)) {
    throw new Error(
      `Cadence ${describeCadence(cadence)} is faster than tier ${tier} floor ${describeCadence(floor)}`,
    );
  }
}
